import { motion } from 'framer-motion'
import { MapPin, Mail, Phone } from 'lucide-react'
import { ContactForm } from '../components/forms/ContactForm'
import { FaqSection } from '../components/faq/FaqSection'
import { useSiteSettings } from '../context/SiteSettingsContext'

const FALLBACK_FAQS = [
  {
    question: 'What industries do you specialise in?',
    answer:
      'We specialise in working with high-value businesses across various sectors including real estate, marble sellers, machine manufacturers, and other businesses operating in the B2B segment. Our systems are designed for businesses where every lead matters.',
  },
  {
    question: 'How long does it take to see results?',
    answer:
      'While initial campaign data can be seen within the first few weeks, we typically advise our clients to expect structured, predictable growth systems to show significant ROI within 60 to 90 days.',
  },
  {
    question: 'Do you offer custom packages?',
    answer:
      "Yes. We don't believe in one-size-fits-all. Every business has unique challenges, so we tailor our BLS Growth Method to fit your specific goals and market dynamics.",
  },
  {
    question: 'What is the minimum budget required?',
    answer:
      'Our strategies are built for scale. We recommend a minimum ad spend budget that allows for proper testing and optimization, which we can discuss during our initial strategy call based on your industry.',
  },
]

export default function ContactPage() {
  const { settings } = useSiteSettings()

  const address =
    [settings?.address_line1, settings?.address_line2, settings?.city, settings?.state, settings?.postal_code]
      .filter(Boolean)
      .join(', ') ||
    '812-814, Western Business Park, Opp. SD Jain School, Vesu, Surat, Gujarat, 395007'
  const email = settings?.email || 'info@digisutra.in'
  const phone = settings?.phone || '+91 8200670752'

  const contactInfo = [
    { icon: MapPin, label: 'Address', value: address },
    { icon: Mail, label: 'Email', value: email, href: `mailto:${email}` },
    { icon: Phone, label: 'Phone', value: phone, href: `tel:${phone.replace(/\s+/g, '')}` },
  ]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="pt-28 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6 lg:px-8"
    >
      <div className="container-wide">
        <motion.div
          className="text-center mb-10 sm:mb-14"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-primary dark:text-white mb-3 sm:mb-4">
            Let's Start the Conversation
          </h1>
          <p className="section-sub max-w-2xl mx-auto">
            If your business is looking for structured marketing systems and predictable lead generation, we would be happy to explore how we can work together.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start mb-16 sm:mb-24">
          <motion.div
            className="space-y-4 sm:space-y-6"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
          >
            {contactInfo.map((item) => (
              <div key={item.label} className="card-glass flex gap-4 p-5 sm:p-6">
                <item.icon className="text-primary dark:text-accent shrink-0 mt-0.5" size={24} strokeWidth={1.8} />
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm sm:text-base">{item.label}</p>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent transition-colors whitespace-pre-line text-sm sm:text-base"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-slate-600 dark:text-slate-400 whitespace-pre-line text-sm sm:text-base">
                      {item.value}
                    </p>
                  )}
                </div>
              </div>
            ))}

            <div className="card-glass p-2 h-64 sm:h-80 overflow-hidden relative group">
              <div className="absolute inset-0 bg-primary/5 dark:bg-accent/5 pointer-events-none z-10 mix-blend-overlay group-hover:opacity-0 transition-opacity duration-500" />
              <iframe
                src={
                  settings?.map_url ||
                  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d930.2608247069479!2d72.77248853597406!3d21.15067508703739!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04deadeec231d%3A0xa589fff5a80ae90d!2sWestern%20Business%20Park!5e0!3m2!1sen!2sin!4v1782467080778!5m2!1sen!2sin'
                }
                width="100%"
                height="100%"
                style={{ border: 0, borderRadius: '0.75rem', filter: 'grayscale(0.2) contrast(1.1)' }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="DigiSutra Location Map"
                className="transition-all duration-500 group-hover:filter-none"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="card-glass p-5 sm:p-6 md:p-8">
              <ContactForm />
            </div>
          </motion.div>
        </div>

        <FaqSection
          location="global"
          title="Frequently Asked Questions"
          subtitle="Find answers to common questions about our services and process."
          fallback={FALLBACK_FAQS}
          schemaId="contact-faq-ld"
        />
      </div>
    </motion.div>
  )
}
